import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HelmetMetaTag from "../Utils/HelmetMetaTag/HelmetMetaTag";
import blogService from "./blogService";

const RecentBlogCard = ({ post, onNavigate }) => {
  const postUrl = `/blog/${post.slug || post.id}`;
  return (
    <article className="cric-blog-card">
      <div
        className="cric-blog-card-media"
        onClick={() => onNavigate(post)}
        style={{ cursor: "pointer" }}
      >
        <img src={post.image} alt={post.title} loading="lazy" />
      </div>
      <div className="cric-blog-card-body">
        <div className="cric-blog-card-tags">
          {post.category.slice(0, 2).map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <h3
          onClick={() => onNavigate(post)}
          style={{ cursor: "pointer" }}
          title={post.title}
        >
          <a
            href={postUrl}
            onClick={(e) => { e.preventDefault(); onNavigate(post); }}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            {post.title}
          </a>
        </h3>
        <p>{post.summary}</p>
        <div className="cric-blog-card-footer">
          <time>{post.date}</time>
          <a
            href={postUrl}
            className="cric-blog-read-btn"
            onClick={(e) => { e.preventDefault(); onNavigate(post); }}
          >
            Read More
          </a>
        </div>
      </div>
    </article>
  );
};

export default function BlogDetailPage({ postData, onBack }) {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(postData || null);
  const [loading, setLoading] = useState(!postData);
  const [error, setError] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    let isMounted = true;
    const identifier = slug || id;

    const fetchData = async () => {
      try {
        // Fetch current post if not passed as prop
        if (!postData && identifier) {
          setLoading(true);
          setError(null);

          let fetched = null;
          if (!isNaN(identifier)) {
            fetched = await blogService.getBlogPostById(identifier);
          } else {
            fetched = await blogService.getBlogPostBySlug(identifier);
          }

          if (isMounted) {
            if (fetched) {
              setPost(fetched);
            } else {
              setError("Blog post not found.");
            }
          }
        }

        // Fetch recent posts/ blogs (get latest 4, filter out current)
        const { posts: allPosts } = await blogService.getBlogPosts({ perPage: 4 });
        if (isMounted) {
          const currentId = postData?.id || post?.id;
          const filtered = allPosts
            .filter((p) => p.id !== currentId && p.slug !== identifier && String(p.id) !== String(identifier))
            .slice(0, 3);
          setRecentPosts(filtered);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error loading blog details:", err);
          if (!postData) setError("Failed to load blog post details.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => { isMounted = false; };
  }, [slug, id]);

  // Fetch recent blogs separately when postData is passed as prop
  useEffect(() => {
    if (!postData) return;
    let isMounted = true;
    blogService.getBlogPosts({ perPage: 4 }).then(({ posts: allPosts }) => {
      if (isMounted) {
        const filtered = allPosts.filter((p) => p.id !== postData.id).slice(0, 3);
        setRecentPosts(filtered);
      }
    }).catch(() => {});
    return () => { isMounted = false; };
  }, [postData]);

  const handleBackNavigation = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/blog");
    }
  };

  const handleNavigateToPost = (targetPost) => {
    const targetSlug = targetPost.slug || targetPost.id;
    navigate(`/blog/${targetSlug}`);
  };

  if (loading) {
    return (
      <div className="col-md-6 col-lg-8 col-12 m-auto my-0 pt-2">
        <div className="cric-blog-detail-page">
          <div className="cric-blog-detail-inner">
            <div className="cric-blog-skeleton-line title" style={{ height: "40px", margin: "20px auto", width: "70%" }} />
            <div className="cric-blog-skeleton-line short" style={{ margin: "10px auto", width: "30%" }} />
            <div className="cric-blog-skeleton-media" style={{ height: "480px", margin: "30px 0" }} />
            <div className="cric-blog-skeleton-line body" />
            <div className="cric-blog-skeleton-line body" />
            <div className="cric-blog-skeleton-line body short" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="col-md-6 col-lg-8 col-12 m-auto my-0 pt-2">
        <div className="cric-blog-detail-page">
          <div className="cric-blog-detail-inner">
            <div className="cric-blog-error-banner" style={{ margin: "40px auto" }}>
              <p>{error || "Blog post not found."}</p>
              <button
                type="button"
                className="cric-blog-read-btn"
                onClick={handleBackNavigation}
                style={{ marginTop: "12px", textDecoration: "underline" }}
              >
                &larr; Back to CricBuddy Blog
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <HelmetMetaTag
        titleName={post.metaTitle || `${post.title} | CricBuddy Blog`}
        desc={post.metaDescription || post.summary || post.title}
        canonical={window.location.href}
        ogtitle={post.metaTitle || `${post.title} | CricBuddy Blog`}
        ogdesc={post.metaDescription || post.summary || post.title}
        ogtype="article"
        ogurl={window.location.href}
        ogimg={post.metaImage || post.image || "https://cricbuddy.in/logo192.png"}
      />

      <div className="col-md-6 col-lg-8 col-12 m-auto my-0 pt-2">
        <section className="cric-blog-detail-page">

          {/* Centered Header: Title, Accent, Meta */}
          <header className="cric-blog-detail-header">
            <h1 className="cric-blog-detail-title">{post.title}</h1>
            <div className="cric-blog-detail-accent">
              <span className="accent-line"></span>
              <span className="accent-line"></span>
            </div>
            <div className="cric-blog-detail-meta">
              <time className="cric-blog-detail-date">{post.date}</time>
              <div className="cric-blog-card-tags">
                {post.category &&
                  post.category.map((cat) => (
                    <span key={cat} className="cric-blog-tag-badge">{cat}</span>
                  ))}
              </div>
            </div>
          </header>

          {/* Full-Width Hero Banner Image */}
          {post.image && (
            <div className="cric-blog-detail-banner">
              <img src={post.image} alt={post.title} />
            </div>
          )}

          {/* Readable Content Area */}
          <div className="cric-blog-detail-inner">
            {/* Author Profile & Social Share Section */}
            <div className="cric-blog-author-share-bar">
              <div className="cric-blog-author-info">
                <img
                  src={post.authorAvatar || "https://secure.gravatar.com/avatar/?s=96&d=mm&r=g"}
                  alt={post.author || "Ronak Shah"}
                  className="cric-blog-author-avatar"
                />
                <span className="cric-blog-author-name">{post.author || "Ronak Shah"}</span>
              </div>
              <div className="cric-blog-social-share">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on LinkedIn"
                  className="cric-social-btn linkedin"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                  </svg>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                  className="cric-social-btn facebook"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7.5v-3H10V9.69c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 3h-2.33v6.8c4.56-.93 8-4.96 8-9.8z"/>
                  </svg>
                </a>
                <a
                  href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Pinterest"
                  className="cric-social-btn pinterest"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M12 2a10 10 0 0 0-3.56 19.34c-.08-.81-.15-2.06.03-2.95l1.09-4.63s-.28-.56-.28-1.39c0-1.3.75-2.28 1.69-2.28.8 0 1.18.6 1.18 1.32 0 .8-.51 2-0.78 3.11-.22.93.47 1.69 1.39 1.69 1.67 0 2.95-1.76 2.95-4.3 0-2.25-1.62-3.82-3.92-3.82-2.67 0-4.24 2-4.24 4.07 0 .8.31 1.67.7 2.14.08.1.09.18.06.31l-.26 1.07c-.04.17-.15.22-.34.13-1.28-.6-2.08-2.46-2.08-3.96 0-3.23 2.35-6.19 6.77-6.19 3.55 0 6.32 2.53 6.32 5.92 0 3.53-2.23 6.37-5.32 6.37-1.04 0-2.02-.54-2.35-1.18l-.64 2.45c-.23.89-.86 2.01-1.28 2.69A10 10 0 1 0 12 2z"/>
                  </svg>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on X"
                  className="cric-social-btn x-twitter"
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
            </div>

            <article
              className="cric-blog-detail-content"
              dangerouslySetInnerHTML={{ __html: post.content || post.summary }}
            />
          </div>

          {/* Recent Blogs Section */}
          {recentPosts.length > 0 && (
            <div className="cric-blog-recent-section">
              <div className="cric-blog-recent-heading">
                <h2>Recent Blogs</h2>
                <span className="cric-blog-recent-accent"></span>
              </div>
              <div className="cric-blog-recent-grid">
                {recentPosts.map((recent) => (
                  <RecentBlogCard
                    key={recent.id}
                    post={recent}
                    onNavigate={handleNavigateToPost}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </React.Fragment>
  );
}
